// WHAT AN EXPORT IS DOING, AND WHAT IT DID — the pieces the Models page and the project
// card both draw.
//
// Owner, 2026-10-25: "show the progress on the model file, under the file name, with
// expand option to see the logs ... make sure every time we click on a model file, we
// have the export history on the right drawer, that shows all exports timeline, and
// history data and file names and sizes and person export it".
//
// THREE COMPONENTS, THREE READS:
//   ExportUnderName   the line under the file name: stage, percent, time left, expand
//   ExportLogPanel    the lines, fetched only when expanded, polled only while running
//   ExportHistoryDrawer  one file's whole timeline, fetched when the drawer opens
//
// Its own file because ModelsPage is already past the hooks lint's path budget (see
// feedback_hooks_lint_path_limit) and because the project card needs the log panel too.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, Progress, Tag, Timeline, Tooltip, Empty, Spin } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { exportRunLog, exportRuns } from "api/projectModels";

const STAGE = { prepare: "Preparing", bank: "Reading the model", publish: "Building 3D", done: "Done" };
const LIVE = ["queued", "running"];

export const isLive = (x) => !!x && LIVE.includes(x.status);

export const eta = (s) => (s > 90 ? `~${Math.round(s / 60)} min left` : s ? `~${s} s left` : "");

export const mb = (b) => (!b ? "" : b >= 1024 * 1024 * 1024
  ? `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
  : `${Math.max(1, Math.round(b / 1024 / 1024))} MB`);

const clock = (s) => {
  if (s == null) return "";
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

const at = (d) => (d ? new Date(d).toLocaleString() : "");

/**
 * THE LOG, fetched only when somebody opens it.
 *
 * While the run is live it polls for what is NEW (?after=) rather than refetching the
 * tail, so a drawer left open for a 30-minute bank costs a few hundred bytes a tick
 * instead of the whole buffer each time. It stops polling the moment the run is not
 * live - a finished log does not change, and a page left open overnight should not keep
 * asking.
 */
export function ExportLogPanel({ projectId, runId, live, height = 240, fetchLog = exportRunLog }) {
  const [lines, setLines] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", truncated: false, status: "" });
  const cursor = useRef(null);
  const box = useRef(null);
  const stick = useRef(true);

  const pull = useCallback(async () => {
    if (!projectId || !runId) return;
    try {
      const out = await fetchLog(projectId, runId, cursor.current);
      cursor.current = out.last || cursor.current;
      setLines((prev) => (cursor.current && prev.length ? [...prev, ...(out.lines || [])] : out.lines || []));
      setState({ loading: false, error: out.error || "", truncated: !!out.truncated, status: out.status });
    } catch (e) {
      setState({ loading: false, error: e.message, truncated: false, status: "" });
    }
  }, [projectId, runId, fetchLog]);

  useEffect(() => {
    cursor.current = null;
    setLines([]);
    setState((s) => ({ ...s, loading: true }));
    pull();
  }, [pull]);

  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(pull, 4000);
    return () => clearInterval(t);
  }, [live, pull]);

  // FOLLOW THE TAIL, BUT ONLY IF THEY ARE AT THE TAIL. Scrolling up to read something
  // and being yanked back down every 4 seconds is the behaviour this guards against.
  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const onScroll = () => {
    const el = box.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  if (state.loading && !lines.length) return <div className="p-2"><Spin size="small" /></div>;

  return (
    <div className="export-log">
      {state.truncated ? (
        <small className="text-muted d-block mb-1">
          Showing the last {lines.length} lines - earlier output is not kept.
        </small>
      ) : null}
      <pre
        ref={box}
        onScroll={onScroll}
        style={{
          maxHeight: height, overflow: "auto", margin: 0, padding: "8px 10px",
          background: "#0f1720", color: "#d6e2ea", borderRadius: 6,
          fontSize: 12, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}
      >
        {lines.length ? lines.map((l, i) => `${l.line}`).join("\n") : "No output yet."}
      </pre>
      {state.error ? <div className="text-danger mt-1"><small>{state.error}</small></div> : null}
    </div>
  );
}

/**
 * THE LINE UNDER THE FILE NAME.
 *
 * Deliberately quiet when there is nothing to say: a file that has never been exported
 * shows nothing at all, because a row of "not exported" on every line is noise. A
 * finished export shows one small line; only a live one takes up a bar.
 */
export function ExportUnderName({ projectId, x, onOpenHistory }) {
  const [open, setOpen] = useState(false);
  if (!x) return null;
  const live = isLive(x);
  // promoted, then failed after: the model is live, only its Project Files trees are missing
  const partial = x.status === "failed" && !!x.published;
  const failed = x.status === "failed" && !partial;

  return (
    <div className="models-name-export">
      {live ? (
        <Tooltip title={eta(x.etaSeconds)}>
          <div style={{ maxWidth: 320 }}>
            <small className="text-muted">
              {STAGE[x.stage] || (x.status === "queued" ? "Waiting to start" : "Exporting")}
              {x.progress ? ` - ${x.progress}%` : ""}
              {x.etaSeconds ? ` - ${eta(x.etaSeconds)}` : ""}
            </small>
            <Progress percent={x.progress} size="small" status="active" />
          </div>
        </Tooltip>
      ) : null}

      {failed ? (
        <small className="d-block text-danger" title={x.error}>
          Export failed{x.stage ? ` while ${String(STAGE[x.stage] || x.stage).toLowerCase()}` : ""}
        </small>
      ) : null}

      {partial ? (
        <small className="d-block text-warning" title={x.error} data-testid="export-partial">
          In 3D{x.exportVersion ? ` (${x.exportVersion})` : ""} - {x.incomplete || "not everything"} incomplete, export again to finish
        </small>
      ) : null}

      {live || failed || partial ? (
        <Tooltip title={open ? "Hide this export's log lines" : "Show this export's log, line by line, as the worker writes it"}>
          <button
            type="button"
            className="integration-link-btn"
            aria-expanded={open}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          >
            {open ? <DownOutlined /> : <RightOutlined />} {open ? "Hide log" : "Show log"}
          </button>
        </Tooltip>
      ) : null}

      {onOpenHistory ? (
        <Tooltip title="Every export of this file, newest first, with its revision and its log">
          <button
            type="button"
            className="integration-link-btn"
            onClick={(e) => { e.stopPropagation(); onOpenHistory(); }}
          >
            History
          </button>
        </Tooltip>
      ) : null}

      {open ? <ExportLogPanel projectId={projectId} runId={x.id} live={live} height={200} /> : null}
    </div>
  );
}

const DOT = { done: "green", failed: "red", running: "blue", queued: "gray" };
// a failed run that had already promoted its version: live, but incomplete
const dotOf = (r) => (r.status === "failed" && r.published ? "orange" : DOT[r.status] || "gray");

/**
 * ONE FILE'S WHOLE EXPORT HISTORY, in the right drawer.
 *
 * Owner's list, verbatim: "all exports timeline, and history data and file names and
 * sizes and person export it". So every row carries the file name AND the version it was
 * at, the size that went in, who pressed it, how long it took and what came out - because
 * "which export produced the revision we are looking at" is the question this answers.
 */
export function ExportHistoryDrawer({ projectId, file, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shown, setShown] = useState("");

  useEffect(() => {
    if (!open || !file) return;
    let alive = true;
    setLoading(true);
    setError("");
    exportRuns(projectId, file._id)
      .then((out) => { if (alive) setRows(out || []); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, file, projectId]);

  const anyLive = rows.some(isLive);
  useEffect(() => {
    if (!open || !anyLive) return undefined;
    const t = setInterval(() => {
      exportRuns(projectId, file._id).then(setRows).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [open, anyLive, projectId, file]);

  return (
    <Drawer
      title={file ? `Export history - ${file.name}` : "Export history"}
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {loading && !rows.length ? <Spin /> : null}
      {error ? <div className="text-danger">{error}</div> : null}
      {!loading && !rows.length && !error ? (
        <Empty description="This file has never been exported to 3D" />
      ) : null}

      <Timeline>
        {rows.map((r) => (
          <Timeline.Item key={r.id} color={dotOf(r)}>
            <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap" }}>
              <b>{r.fileName}</b>
              {r.fileVersion ? <Tag color="blue">v{r.fileVersion}</Tag> : null}
              <Tag color={{ green: "green", red: "red", orange: "orange" }[dotOf(r)] || "blue"}>
                {r.status === "done" ? "In 3D" : dotOf(r) === "orange" ? "In 3D - incomplete" : r.status}
              </Tag>
            </div>
            {dotOf(r) === "orange" ? (
              <small className="d-block text-warning">{r.incomplete || "Not everything"} did not finish - export again to finish it</small>
            ) : null}

            <small className="d-block text-muted">
              {at(r.finishedAt || r.startedAt || r.queuedAt)}
              {r.by ? ` - by ${r.by}` : ""}
              {r.sizeBytes ? ` - ${mb(r.sizeBytes)}` : ""}
              {r.durationSeconds != null ? ` - took ${clock(r.durationSeconds)}` : ""}
            </small>

            {r.exportVersion ? (
              <small className="d-block text-muted">Revision {r.exportVersion}</small>
            ) : null}

            {isLive(r) ? (
              <div style={{ maxWidth: 360 }}>
                <Progress percent={r.progress} size="small" status="active" />
                <small className="text-muted">{STAGE[r.stage] || r.stage} {eta(r.etaSeconds)}</small>
              </div>
            ) : null}

            {r.error ? <div className="text-danger"><small>{r.error}</small></div> : null}
            {(r.warnings || []).map((w) => (
              <div key={w}><small className="text-warning">{w}</small></div>
            ))}

            {/* the files that went in with it - a missing link is why a revision can look wrong */}
            {(r.parts || []).length ? (
              <small className="d-block text-muted">
                {r.parts.filter((p) => p.found).length}/{r.parts.length} linked files included
                {r.parts.some((p) => !p.found)
                  ? ` - missing: ${r.parts.filter((p) => !p.found).map((p) => p.name).join(", ")}`
                  : ""}
              </small>
            ) : null}

            <Tooltip title={shown === r.id ? "Hide this run's log" : "Show what this run wrote while it exported"}>
              <button
                type="button"
                className="integration-link-btn"
                onClick={() => setShown(shown === r.id ? "" : r.id)}
              >
                {shown === r.id ? "Hide log" : `Show log${r.logLines ? ` (${r.logLines} lines)` : ""}`}
              </button>
            </Tooltip>
            {shown === r.id ? (
              <ExportLogPanel projectId={projectId} runId={r.id} live={isLive(r)} height={220} />
            ) : null}
          </Timeline.Item>
        ))}
      </Timeline>
    </Drawer>
  );
}

export default ExportUnderName;
